import React, { useEffect , useState, useMemo, useContext} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { LearnersList } from "./components/learners";
import { EditSimulationReportsDetail } from "./components/detail";
import { fetchCourseLearners, fetchLearningRecordSimulation, fetchTranscriptsForLearningSimulation } from "src/redux/actions/coaching/learners";
import { fetchDraftSimulation, fetchTranscriptsForSimulation } from "src/redux/actions/coaching/simulation";

import { Simulation } from "src/types/coaching/simulation";
import InfiniteScroll from "src/components/infiniteScroll";
import { SimulationReportContext, SimulationReportContextProps } from "./context";
import { CallRecording } from "./components/recordings";

import _ from "lodash";
import cx from "classnames";
import styles from "./reports.module.scss";

export const EditSimulationsReportsPage: React.FC<object> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const params = useParams();
  const id = params.id as string;
  const learningId = params.learningId as string;

  const loading = useSelector((state: any) => {
    return _.get(state.coaching, `simulations.attemptLearners.loading`);
  });

  const [contextState, setContextState] = useState<SimulationReportContextProps>({
    showCallRecording: false,
    toggleCallRecording: () => {}
  });

  useEffect(() => {
    setContextState(prev => ({
      ...prev,
      toggleCallRecording: () => {
        setContextState(prevState => ({
          ...prevState,
          showCallRecording: !prevState.showCallRecording,
        }));
      },
    }));
  }, []);

  const simulation: Simulation = useSelector(
    (state: any) =>
      _.get(state.coaching, `simulations.simulation[${id}].item`) as Simulation,
  );
  
  const simulationLoading = useSelector((state: any) => {
    return _.get(state.coaching, `simulations.simulation[${id}].loading`);
  });

  const {
    learnersLoading,
    learnersLoaded,
    learners,
    totalRecordsCount,
    currentPage,
    nextPage,
    hasMoreItems,
  } = useSelector((state: any) => {
    const learnersState = _.get(state.courses, "learners") || {};
    const loading = _.get(learnersState, "loading");
    const loaded = _.get(learnersState, "loaded");
    const pagination = _.get(learnersState, "pagination");
    const totalRecordsCount = _.get(pagination, "totalRecordsCount", 0);
    const currentPage = _.get(pagination, "currentPage", 0);
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    const learners = _.get(learnersState, "items") || [];

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      learnersLoading: loading,
      learnersLoaded: loaded,
      learners: learners,
      totalRecordsCount: totalRecordsCount,
      currentPage: currentPage,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems,
    };
  }, _.isEqual);


  const learner = learners.find((item: any) => item.id === learningId);

  const [selectedAttempt, setSelectedAttempt] = useState<any>(learner);

  const fetchingTranscripts = useSelector((state: any) => {
    const fetching = _.get(state.coaching, `simulations.transcripts.[${selectedAttempt?.id}].fetching`);
    return fetching;
  });
  
  const transcripts = useSelector((state: any) => {
    return _.get(state.coaching, `simulations.transcripts.[${selectedAttempt?.id}].item`);
  });

  const learningRecord = useSelector((state: any) => {
    return _.get(state.coaching, `simulations.learnerReport.item`);
  });

  const handleLoadMoreLearners = () => {
    if (nextPage && simulation) {
      const request = {
        course: simulation.id,
        status: "COMPLETED",
        page: nextPage,
        limit: 10,
      };
      dispatch(fetchCourseLearners(request));
    }
  };

  useEffect(() => {
    if (id && !simulation) {
      dispatch(fetchDraftSimulation(id));
    }

    if (id) {
      const request = {
        course: id,
        status: "COMPLETED",
        page: 0,
        limit: 10,
      };

      dispatch(fetchCourseLearners(request));
    }
  }, [id]);


  useEffect(() => {
    setContextState((prev) => {
      return {
        ...prev,
        simulation: simulation
      }
    });
  }, [simulation]);

  useEffect(() => {
    setContextState(prev => ({
      ...prev,
      learners,
    }));
  }, [learners]);

  useEffect(() => {
    setContextState(prev => ({
      ...prev,
      fetchTranscripts: handleFetchTranscripts,
    }));
  }, [learner]);

  useEffect(() => {
    setContextState((prev) => {
      return {
        ...prev,
        fetchingTranscripts: fetchingTranscripts,
        transcripts: transcripts
      }
    });
  }, [fetchingTranscripts, transcripts]);

  useEffect(() => {
    setContextState((prev) => {
      return {
        ...prev,
        learningRecord: learningRecord,
      }
    });
  }, [learningRecord]);

  const handleFetchTranscripts = (attempt?: any) => {
    setSelectedAttempt(attempt)
    const attemptId = attempt?.id || learner?.id; 
    if (!attemptId) return;

    dispatch(fetchTranscriptsForSimulation(attemptId));
  }
  
  return (
    <SimulationReportContext.Provider value={contextState}>
    <div
      className={cx(styles.reportsContainer, {
        [styles.sidebarCollapsed]: contextState.showCallRecording,
      })}
    >
      <div className={styles.header}>
        <div className={styles.backContainer}>
          <div
            className={styles.back}
            onClick={() => {
              navigate(`/coaching/manage/simulations/draft/${id}`);
            }}
          >
            <i className="paperflite-left-arrow"></i>
            <span>simulations</span>
          </div>
        </div>
      </div>

      <div className={styles.bodyWrapper}>
        <div className={styles.left}>
          <div className={styles.title}>
            completed learners ({_.size(learners)})
          </div>
          <div
            className={cx(styles.contentsList, {
              [styles.slideOut]: contextState.showCallRecording,
            })}
          >
            <InfiniteScroll
              className={styles.scroll}
              loading={learnersLoading}
              hasMore={hasMoreItems}
              loadMore={handleLoadMoreLearners}
              loader={() => {
                return _.times(
                  _.size(learners) === 0 || currentPage === 0 ? 7 : 1,
                  (index) => <LearnerPlaceholder key={index} />,
                );
              }}
            >
              <LearnersList />
            </InfiniteScroll>
          </div>
        </div>
        <div className={styles.right}>
          <div className={styles.detailContainer}>
            {(loading || simulationLoading) && !learner && <SimulationReportPlaceholder />}
            {learner && <EditSimulationReportsDetail learner={learner}/>}
          </div>
        </div>

        {contextState.showCallRecording && (
        <div className={styles.callRecordingWrapper}>
          <CallRecording learner={learner} />
        </div>
        )}
      </div>
    </div>
    </SimulationReportContext.Provider>
  );
};

export const LearnerPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.stacks, styles.placeholder)}>
      <div className={cx(styles.name, styles.block)}></div>
      <div className={styles.info}>
        <div className={cx(styles.block, styles.blockName)}></div>
        <div className={cx(styles.block, styles.blockDescription)}></div>
      </div>
    </div>
  );
};

export const SimulationReportPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.detailContainer, styles.placeholder)}>
      <div className={styles.headerContainer}>
        <div className={cx(styles.name, styles.block)}></div>
        <div className={styles.info}>
          <div className={cx(styles.title, styles.block)}></div>
          <div className={cx(styles.subTitle, styles.block)}></div>
      </div>
      </div>
      <div className={styles.content}>
        <div className={styles.card}></div>
        <div className={styles.card}></div>
        <div className={styles.card}></div>
      </div>
    </div>
  );
};